/**
 * One style choice of the look — the kind of chair, how tall the flowers
 * stand — as a row of pills. The first pill is "not chosen here": what is in
 * force without a choice (the event's, the pictures', the default), named so
 * nobody wonders what it will be.
 */
export interface DecorChoiceProps<T extends string> {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T | undefined;
  onChange: (next: T | undefined) => void;
  /** The first pill: "Event's: Chiavari", "Auto: Round back". */
  inheritedLabel: string;
}

const PILL =
  'min-h-11 sm:min-h-0 sm:h-8 px-3 rounded-full text-sm border transition-colors whitespace-nowrap';
const ON = 'bg-gray-900 border-gray-900 text-gray-50';
const OFF = 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50';

export function DecorChoice<T extends string>({
  label,
  options,
  value,
  onChange,
  inheritedLabel,
}: DecorChoiceProps<T>) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
        <button
          type="button"
          aria-pressed={value === undefined}
          onClick={() => onChange(undefined)}
          className={`${PILL} ${value === undefined ? ON : OFF} italic`}
        >
          {inheritedLabel}
        </button>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={value === o.value}
            onClick={() => onChange(o.value)}
            className={`${PILL} ${value === o.value ? ON : OFF}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
